import { createActionFactory } from '../../common/redux';

const { createAction, createApiFailureAction } = createActionFactory('Common Entities => Integrations => ');

// ################################################################################################################
// FETCH INTEGRATIONS
// ################################################################################################################
const fetchIntegrations = createAction('fetch integrations', ({ containerRef, classRef, typeRef }) => ({
  containerRef,
  classRef,
  typeRef,
}));
const fetchIntegrationsSuccess = createAction('successfully fetched integrations', (integrations) => ({
  integrations,
}));
const fetchIntegrationsFailure = createApiFailureAction('failed to fetch integrations');

// ################################################################################################################
// FETCH INTEGRATION TEMPLATES
// ################################################################################################################
const fetchIntegrationTemplates = createAction('fetch integration templates', (containerRef, classRef) => ({
  containerRef,
  classRef,
}));
const fetchIntegrationTemplatesSuccess = createAction(
  'successfully fetched integration templates',
  (integrationTemplates) => ({
    integrationTemplates,
  })
);
const fetchIntegrationTemplatesFailure = createApiFailureAction('failed to fetch integration templates');

// ################################################################################################################
// CREATE INTEGRATION FROM TEMPLATE
// ################################################################################################################
const createFromTemplate = createAction(
  'create integration from template',
  (containerRef, templateId, context, wizardFields) => ({
    containerRef,
    templateId,
    context,
    wizardFields,
  })
);
const createFromTemplateSuccess = createAction('create integration from template success', (integration) => ({
  integration,
}));
const createFromTemplateFailure = createApiFailureAction('create integration from template failure');

// ################################################################################################################
// UPDATE INTEGRATIONS
// ################################################################################################################

const updateIntegration = createAction('update integration', (integration) => ({ integration }));
const updateIntegrationSuccess = createAction('update integration success', (integration) => ({ integration }));
const updateIntegrationFailure = createApiFailureAction('update integration failure');

//allows us to set an integration to the value of a specific integration object
const setIntegration = createAction('setting the values of an integration', (integration) => ({ integration }));

const updateAndActivateIntegration = createAction('update and activate integration', (integration) => ({
  integration,
}));
const updateAndActivateIntegrationSuccess = createAction('update and activate integration success', (integration) => ({
  integration,
}));
const updateAndActivateIntegrationFailure = createApiFailureAction('update integration failure');

const enableIntegration = createAction('enable integration', (integrationId) => ({ integrationId }));
const enableIntegrationSuccess = createAction('enable integration success');
const enableIntegrationFailure = createApiFailureAction('enable integration failure', (integrationId) => ({
  integrationId,
}));

const requestDisableIntegration = createAction('request disable integration', (integrationId, classRef) => ({
  integrationId,
  classRef,
}));
const disableIntegration = createAction('disable integration', (integrationId) => ({ integrationId }));
const disableIntegrationSuccess = createAction('disable integration success');
const disableIntegrationFailure = createApiFailureAction('disable integration failure', (integrationId) => ({
  integrationId,
}));

const deleteIntegration = createAction('delete integration', (integrationId) => ({ integrationId }));
const deleteIntegrationSuccess = createAction('delete integration success', (integrationId) => ({
  integrationId,
}));
const deleteIntegrationFailure = createApiFailureAction('delete integration failure');

// ################################################################################################################
// RESET INTEGRATIONS
// ################################################################################################################
const reset = createAction('reset integrations');

// ################################################################################################################
// UPDATE INTEGRATIONS BY ID
// ################################################################################################################
const updateIntegrationFieldById = createAction('update integration field', (fieldId, value, integrationId) => ({
  fieldId,
  value,
  integrationId,
}));

// ################################################################################################################
// WIZARD INTEGRATIONS ACTIONS
// ################################################################################################################
const openIntegrationWizard = createAction('open integration wizard', (wizardTemplate) => ({ wizardTemplate }));
const startWizardLoading = createAction('start wizard loading');
const addWizardCredentialsData = createAction('add wizard credentials data', (credentails) => ({ credentails }));
const clearWizardData = createAction('clear integration wizard data');
const showWizardCredentialsError = createAction('show wizard credentials error');
const setActiveIntegrationTemplate = createAction(
  'set active integration template for the integration designer',
  (wizardTemplate) => ({ wizardTemplate })
);
const addWizardApiDefinition = createAction('add wizard api definition', (apiUrl) => ({ apiUrl }));
const addWizardCampaignId = createAction('add wizard campaign id', (id) => ({ id }));
const updateIntegrationWizardField = createAction('update integration wizard field', (fieldId, value) => ({
  fieldId,
  value,
}));
const addWizardSteps = createAction('add wizard steps', (steps) => ({ steps }));
const addWizardFields = createAction('add wizard fields to prepare initial request object', (blueprintData) => ({
  blueprintData,
}));
const addWizardDefaultMapping = createAction('set wizard default mapping', (defaultFields, hasMandatoryRow) => ({
  defaultFields,
  hasMandatoryRow,
}));
const addWizardAPIMapping = createAction(
  'set wizard API mapping',
  (mandatoryFields, defaultFields, isInitialAPISetup, isTypeahead) => ({
    mandatoryFields,
    defaultFields,
    isInitialAPISetup,
    isTypeahead,
  })
);
const addWizardMapping = createAction('set wizard mapping from the blueprintData', (blueprintData) => ({
  blueprintData,
}));
const updateWizardMappingField = createAction('update wizard mapping field', (idx, value, fieldName, apiRequired) => ({
  idx,
  value,
  fieldName,
  apiRequired,
}));
const updateWizardMappingRequiredField = createAction('set wizard apiRequired mapping field', (idx) => ({ idx }));
const deleteMappingRow = createAction('delete a wizard mapping row', (idx) => ({ idx }));
const addMappingRow = createAction('add a wizard mapping row');

const validateFormAndGoNext = createAction('validate a form and go to the next wizard step', (formId) => ({ formId }));
const toggleAllowSwitchToNextStep = createAction('toggle allow switching to the next wizard step', (allowNextStep) => ({
  allowNextStep,
}));

const fetchDataSource = createAction('request source data for a wizard step', (fieldId, url) => ({ fieldId, url }));
const fetchDataSourceSuccess = createAction('request source data for a wizard step success', (fieldId, data) => ({
  fieldId,
  data,
}));
const fetchDataSourceFailure = createApiFailureAction('request source data for a wizard step failure', (fieldId) => ({
  fieldId,
}));

const setInitialAPISetup = createAction('set initial API setup', (isInitial) => ({ isInitial }));

const fetchMappingData = createAction('request mapping data for a wizard', (url) => ({ url }));
const fetchMappingDataSuccess = createAction('request mapping data for a wizard step success', (data) => ({ data }));
const fetchMappingDataFailure = createApiFailureAction('request mapping data for a wizard step failure');

const fetchIntegrationCredentials = createAction('fetch integration credentials');
const fetchIntegrationCredentialsSuccess = createAction('fetch integration credentials success', (credentials) => ({
  credentials,
}));
const fetchIntegrationCredentialsFailure = createApiFailureAction('fetch integration credentials failure');

const saveIntegrationCredentials = createAction('save integration credentials', (credentialsFields, isCreating) => ({
  credentialsFields,
  isCreating,
}));
const saveIntegrationCredentialsSuccess = createAction('save integration credentials success', (credentials) => ({
  credentials,
}));
const saveIntegrationCredentialsFailure = createApiFailureAction('save integration credentials failure');

const deleteIntegrationCredentials = createAction('delete integration credentials', (credentialsId) => ({
  credentialsId,
}));
const deleteIntegrationCredentialsSuccess = createAction('delete integration credentials success', (credentialsId) => ({
  credentialsId,
}));
const deleteIntegrationCredentialsFailure = createApiFailureAction('delete integration credentials failure');
const fetchAdobeAttributeFailure = createApiFailureAction('fetch adobe attribute failure');

export default {
  fetchIntegrations,
  fetchIntegrationsSuccess,
  fetchIntegrationsFailure,

  fetchIntegrationTemplates,
  fetchIntegrationTemplatesSuccess,
  fetchIntegrationTemplatesFailure,

  createFromTemplate,
  createFromTemplateSuccess,
  createFromTemplateFailure,

  updateIntegration,
  updateIntegrationSuccess,
  updateIntegrationFailure,

  setIntegration,

  updateAndActivateIntegration,
  updateAndActivateIntegrationSuccess,
  updateAndActivateIntegrationFailure,

  enableIntegration,
  enableIntegrationSuccess,
  enableIntegrationFailure,

  requestDisableIntegration,
  disableIntegration,
  disableIntegrationSuccess,
  disableIntegrationFailure,

  deleteIntegration,
  deleteIntegrationSuccess,
  deleteIntegrationFailure,

  updateIntegrationFieldById,

  reset,

  addWizardApiDefinition,
  addWizardCampaignId,
  openIntegrationWizard,
  startWizardLoading,
  addWizardCredentialsData,
  clearWizardData,
  showWizardCredentialsError,
  setActiveIntegrationTemplate,
  updateIntegrationWizardField,
  addWizardSteps,
  addWizardFields,
  addWizardMapping,
  addWizardDefaultMapping,
  addWizardAPIMapping,
  updateWizardMappingField,
  updateWizardMappingRequiredField,
  deleteMappingRow,
  addMappingRow,

  validateFormAndGoNext,
  toggleAllowSwitchToNextStep,
  setInitialAPISetup,

  fetchDataSource,
  fetchDataSourceSuccess,
  fetchDataSourceFailure,

  fetchMappingData,
  fetchMappingDataSuccess,
  fetchMappingDataFailure,

  fetchIntegrationCredentials,
  fetchIntegrationCredentialsSuccess,
  fetchIntegrationCredentialsFailure,
  saveIntegrationCredentials,
  saveIntegrationCredentialsSuccess,
  saveIntegrationCredentialsFailure,
  deleteIntegrationCredentials,
  deleteIntegrationCredentialsSuccess,
  deleteIntegrationCredentialsFailure,

  fetchAdobeAttributeFailure,
};
